import type { ReactNode } from 'react';

/** Shared count-badge red — `Sidebar.tsx`'s `SIDEBAR_PILLAR.military.primary` is the same value
 *  but is a distinct, private palette (CLAUDE.md: "distinct sets, do not merge"), so this shell
 *  badge color is its own small shared constant rather than reaching into that one. */
export const BADGE_RED = '#c45a4a';

/**
 * SF-122: the reusable card primitive every app-shell page ("glance view") builds on. Renders
 * `title` as a real heading (never a styled `<span>`) so screen readers get heading navigation
 * between cards — both `MobileThroneShell` and `DesktopThroneShell` render exactly one page-level
 * `<h1>` that every `<GlanceCard>` nests under, so `as` defaults to `'h2'` everywhere in this
 * spec's implementation.
 */
interface GlanceCardProps {
  title: string;
  as?: 'h2' | 'h3';
  icon?: ReactNode;
  headerAction?: ReactNode;
  badge?: number;
  children?: ReactNode;
}

export function GlanceCard({ title, as = 'h2', icon, headerAction, badge, children }: GlanceCardProps) {
  const Heading = as;

  return (
    <div className="rounded-lg p-4 bg-game-800 border border-game-700">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {icon && (
            <span aria-hidden="true" className="flex-shrink-0">
              {icon}
            </span>
          )}
          <Heading className="text-sm font-semibold text-game-50 truncate">{title}</Heading>
          {badge != null && badge > 0 && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                backgroundColor: BADGE_RED,
                color: '#fff',
                borderRadius: 8,
                padding: '1px 6px',
                minWidth: 16,
                textAlign: 'center',
                flexShrink: 0,
              }}
            >
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </div>
        {headerAction && <div className="text-xs flex-shrink-0">{headerAction}</div>}
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}
